/*
 * THE GO-LIVE -> IN-SERVICE HANDOFF PROMPT — #435 surface 2 (mockups handoff-01,
 * handoff-04). The calm, once-per-product moment a bounded project ships and
 * becomes an ongoing service.
 *
 * It is DERIVED, not a setting to hunt for: it surfaces on the board only when the
 * project looks ready (handoffReadiness → ready/provisional), using the app's own
 * confirm pattern — never window.confirm (R24). It states exactly what gets frozen
 * before the one click, so the irreversible-feeling step is never a surprise (R15);
 * the flip itself is reversible-framed (the record is kept, not deleted), so it is
 * the one primary action, not a destructive red one (R17).
 *
 * Two faces, one shape:
 *  - READY (date reached AND every delivery phase done) / READY AHEAD (all done
 *    before the planned date): a confident teal eyebrow; the snapshot is final.
 *  - GO-LIVE DATE REACHED, a phase still open: an amber eyebrow that names the open
 *    phase and its progress, labels the snapshot provisional, and offers "Open the
 *    plan" alongside — Hypercare after go-live is legitimate, so this informs and
 *    never blocks (the honest variant, handoff-04).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../store';
import Icon from './Icon';
import DateField from './DateField';
import { useDismiss, useFocusTrap } from '../motion';
import { fmtMoney } from '../money';
import { humanDay } from '../when';
import { can } from '../permissions';
import {
  livePhases,
  phaseShownStatus,
  openPhaseSummary,
  dayFromMs,
  goLiveAnchorDay,
} from '../phases';
import { handoffReadiness, handoffOffered, wholeDaysBetween, type HandoffReadiness } from '../handoff';
import { handoffDismissed } from '../handoffDismissals';

function dayToMs(day: string): number | undefined {
  const t = Date.parse(`${day}T00:00:00.000Z`);
  return Number.isNaN(t) ? undefined : t;
}

/** Today as a plain UTC day in ms — the default actual go-live (decision 1: a team
 *  often marks it the day they do it, and can edit it back to the real day). */
function todayMs(): number {
  return dayToMs(dayFromMs(Date.now())) ?? Date.now();
}

/** "20 days late" / "6 days early" / "on the planned day" — the schedule variance,
 *  phrased for a reader, with the tone the one .rag badge should carry (R3). Tones
 *  are the app's rag modifiers: a = amber, g = green, n = neutral. Uses the shared
 *  whole-UTC-day count so this badge, the overdue nudge and the frozen snapshot can
 *  never read a different number of days for the same two dates. */
function varianceWord(
  planned: number | undefined,
  actual: number,
): { text: string; tone: 'a' | 'g' | 'n' } {
  if (planned == null) return { text: 'no planned date', tone: 'n' };
  const days = wholeDaysBetween(planned, actual);
  if (days === 0) return { text: 'on the planned day', tone: 'g' };
  if (days > 0) return { text: `${days} ${days === 1 ? 'day' : 'days'} late`, tone: 'a' };
  return { text: `${-days} ${-days === 1 ? 'day' : 'days'} early`, tone: 'g' };
}

export default function HandoffPrompt() {
  const meta = useApp((s) => s.meta);
  const projectId = useApp((s) => s.projectId);
  const project = useApp((s) => s.projects.find((p) => p.id === s.projectId));
  const phases = useApp((s) => s.phases);
  const issues = useApp((s) => s.issues);
  const milestones = useApp((s) => s.milestones);
  const markLive = useApp((s) => s.markLiveAndMoveToService);
  const setView = useApp((s) => s.setView);
  const role = useApp((s) => s.shownRole);
  // A persistent "Mark it live" control (the board ribbon, the Portfolio nudge)
  // can re-request the dialog for this board — that overrides a "Not yet" from
  // earlier this session, so the prompt is never a one-shot a person can't reopen.
  const handoffRequestBoard = useApp((s) => s.handoffRequestBoard);
  const setHandoffRequest = useApp((s) => s.setHandoffRequest);

  // Re-render the gate when a dismissal happens, without persisting it.
  const [, force] = useState(0);
  const [actual, setActual] = useState<number>(todayMs);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');

  const now = Date.now();
  const livePh = useMemo(() => livePhases(phases), [phases]);
  // A phase's done-ness is the SHOWN status (a PM override, else card-derived) —
  // the exact rule the Plan and the Portfolio read (phaseShownStatus), so "all
  // phases done" here can never disagree with what the person sees as done there.
  const derivedPhases = useMemo(
    () => livePh.map((p) => ({ status: phaseShownStatus(p, issues) })),
    [livePh, issues],
  );
  // A board is a project to hand off only if it is actually run as one — a plan is
  // set up (a go-live, a phase, or a budget). Mirrors portfolio.ts `tracked`.
  const tracked =
    project != null && (project.goLive != null || livePh.length > 0 || project.budget != null);

  // The planned go-live the dialog reads is the ANCHOR-resolved day (0217), so the
  // readiness, the displayed date and the variance all match the ribbon that opened it.
  const plannedMs = useMemo(() => {
    if (!project) return undefined;
    const day = goLiveAnchorDay(project, livePh, (id) => milestones.find((m) => m.id === id)?.targetDay);
    return day ? dayToMs(day) : project.goLive;
  }, [project, livePh, milestones]);

  const readiness: HandoffReadiness = handoffReadiness({
    deliveryMode: meta.deliveryMode,
    tracked,
    actualGoLive: meta.actualGoLive,
    plannedGoLive: plannedMs,
    phases: derivedPhases,
    nowMs: now,
  });

  // The scope figures the snapshot will freeze, read from the live board.
  const scope = useMemo(() => {
    const committed = issues.filter((i) => i.status !== 'backlog');
    const shipped = committed.filter((i) => i.status === 'done').length;
    const cut = committed.filter((i) => i.status === 'wontdo').length;
    const open = committed.length - shipped - cut; // carried into service
    return { total: committed.length, shipped, cut, open, deferred: committed.length - shipped };
  }, [issues]);

  // The delivery phase the honest variant names — earliest not-done, with honest
  // counts (openPhaseSummary, tested in test-handoff). Its card count is that phase's
  // own tags; the board-wide carryover is scope.open, never pinned to a phase, so an
  // untagged-but-open board can't read "0 of 0 done" beside "6 cards carry over".
  const openPhase = useMemo(() => openPhaseSummary(livePh, issues), [livePh, issues]);

  // A request for THIS board (ribbon/nudge) forces the prompt open even after a
  // "Not yet" this session; otherwise the auto-prompt shows until dismissed.
  const forced = handoffRequestBoard === projectId;
  const offered = handoffOffered(readiness) && (forced || !handoffDismissed.has(projectId));
  // The handoff is a board-settings change, so it needs the same right the delivery-
  // mode card in Settings needs — manager and up (can editSettings).
  if (!offered || !can(role, 'editSettings')) return null;

  const provisional = readiness.state === 'provisional';
  const phaseOpen = readiness.state === 'provisional' && readiness.reason === 'phase-open';
  // A project tracked by a go-live but with no delivery phases: the lead must not
  // claim "every delivery phase is done" when there are none to speak of.
  const noPhases = derivedPhases.length === 0;

  // The as-delivered record measures slip against the COMMITTED baseline (0217 / pm R1a) —
  // the frozen `project.goLive`, which does not move when a phase/milestone anchor's target
  // moves — NOT the live derived day (that drives readiness/overdue above). For a fixed or
  // unanchored go-live the two are identical. Fall back to the derived day only if, somehow,
  // no committed date was ever frozen.
  const planned = project?.goLive ?? plannedMs ?? undefined;
  const variance = varianceWord(planned, actual);
  const baseline = project?.budget ?? undefined;
  const forecast = project?.forecast ?? undefined;
  const budgetPct =
    baseline != null && baseline > 0 && forecast != null
      ? Math.round(((forecast - baseline) / baseline) * 100)
      : null;

  const dismiss = () => {
    handoffDismissed.add(projectId);
    // Clear a standing request so "Not yet" actually closes it; the ribbon/nudge
    // can set it again to reopen.
    if (forced) setHandoffRequest(null);
    force((n) => n + 1);
  };

  const confirm = async () => {
    if (busy) return;
    setBusy(true);
    setProblem('');
    try {
      await markLive({
        actualGoLive: actual,
        plannedGoLive: planned ?? null,
        budgetAtGoLive: baseline ?? null,
        provisional,
      });
      setHandoffRequest(null);
      // The board is now in service; the prompt's condition (actualGoLive unset) no
      // longer holds, so it closes itself and the one-time ribbon takes over.
    } catch {
      setProblem('That could not be saved. Try again.');
      setBusy(false);
    }
  };

  return (
    <Dialog
      name={meta.name}
      phaseOpen={phaseOpen}
      provisional={provisional}
      noPhases={noPhases}
      openPhase={openPhase}
      planned={planned}
      actual={actual}
      setActual={setActual}
      variance={variance}
      scope={scope}
      baseline={baseline}
      forecast={forecast}
      budgetPct={budgetPct}
      busy={busy}
      problem={problem}
      onDismiss={dismiss}
      onConfirm={confirm}
      onOpenPlan={() => { dismiss(); setView('plan'); }}
    />
  );
}

type DialogProps = {
  name: string;
  phaseOpen: boolean;
  provisional: boolean;
  noPhases: boolean;
  openPhase: {
    name: string;
    started: boolean;
    total: number;
    done: number;
    pct: number;
    remaining: number;
    phasesTotal: number;
  } | null;
  planned?: number;
  actual: number;
  setActual: (ms: number) => void;
  variance: { text: string; tone: 'a' | 'g' | 'n' };
  scope: { total: number; shipped: number; cut: number; open: number; deferred: number };
  baseline?: number;
  forecast?: number;
  budgetPct: number | null;
  busy: boolean;
  problem: string;
  onDismiss: () => void;
  onConfirm: () => void;
  onOpenPlan: () => void;
};

function Dialog(p: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  // Reuse the app's leave animation; Escape and the backdrop both mean "Not yet".
  const { leaving, dismiss } = useDismiss(p.onDismiss);
  useFocusTrap(panelRef, true);

  // Topmost layer: capture-phase Escape with stopImmediatePropagation so a card or
  // form beneath never also reacts to the same key (nested-overlay rule, ConfirmHost).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); dismiss(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [dismiss]);

  // Ready-ahead (all phases done before the planned date) must not claim the date
  // was reached — it wasn't; the work simply finished early.
  const eyebrow = p.phaseOpen
    ? 'Go-Live date reached'
    : p.provisional
      ? 'Ready ahead of plan'
      : 'Go-Live reached';
  const title = p.phaseOpen
    ? `${p.name} has reached its Go-Live date`
    : `${p.name} is ready to go into service`;
  return createPortal(
    <div className={`overlay ${leaving ? 'is-leaving' : ''}`} onClick={dismiss}>
      <div
        ref={panelRef}
        className="modal ho-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ho-title"
        // tabIndex so useFocusTrap's root.focus() lands focus IN the dialog on
        // open (a plain <div> can't hold focus otherwise) — the pattern the card
        // modal, ConfirmHost and the palette all use (L139).
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-body ho-body">
          <div className={`ho-eyebrow ${p.phaseOpen ? 'is-caution' : 'is-ready'}`}>
            <Icon name="clock" size={14} />
            <span>{eyebrow}</span>
          </div>
          <h2 id="ho-title" className="ho-title">{title}</h2>
          <p className="ho-lead">
            {p.phaseOpen ? (
              <>The planned Go-Live date has passed, but one delivery phase is still running. You can
              still mark it live now — Hypercare often continues after Go-Live — or finish the phase first.</>
            ) : p.provisional ? (
              <>Every delivery phase is done, ahead of the planned Go-Live date. Mark it live to record
              the delivery and move this board into ongoing service — this happens once, for the product's life.</>
            ) : p.noPhases ? (
              <>Its planned Go-Live date has passed. Mark it live to record the delivery and move this
              board into ongoing service — this happens once, for the product's life.</>
            ) : (
              <>Its planned Go-Live date has passed and every delivery phase is done. Mark it live to
              record the delivery and move this board into ongoing service — this happens once, for the product's life.</>
            )}
          </p>

          {p.phaseOpen && p.openPhase && (
            <div className="ho-phase">
              <div className="ho-phase-head">
                <b>{p.openPhase.name} — {p.openPhase.started ? 'still in progress' : 'not started'}</b>
                {p.openPhase.total > 0 && <span className="rag a">{p.openPhase.pct}%</span>}
              </div>
              {p.openPhase.total > 0 && (
                <div className="ho-phase-bar" aria-hidden="true">
                  <span style={{ width: `${p.openPhase.pct === 0 ? 0 : Math.max(2, Math.min(100, p.openPhase.pct))}%` }} />
                </div>
              )}
              <div className="ho-phase-note">
                {p.openPhase.total > 0
                  ? `${p.openPhase.done} of ${p.openPhase.total} cards done`
                  : 'no cards tracked to this phase yet'}
                {' · '}
                {p.openPhase.remaining === 1
                  ? 'the last delivery phase still open'
                  : `${p.openPhase.remaining} of ${p.openPhase.phasesTotal} phases still open`}
              </div>
            </div>
          )}

          <div className="ho-snap-label">
            {p.phaseOpen ? 'What would be frozen if you mark it live now' : 'What gets frozen as the as-delivered record'}
          </div>
          <div className="ho-snap">
            <div className="ho-snap-row">
              <span className="ho-snap-k">Go-Live · planned → actual</span>
              <span className="ho-snap-v">
                <span className="ho-snap-dates">
                  {p.planned != null ? humanDay(dayFromMsLocal(p.planned)) : '—'} →{' '}
                  <DateField
                    variant="form"
                    ariaLabel="Actual Go-Live date"
                    value={dayFromMsLocal(p.actual)}
                    // Both paths update the actual date so the "N days late" badge tracks
                    // every change — a calendar pick fires onChange (not onCommit), and
                    // without this the badge sat stale until the field was blurred.
                    onChange={(day) => { const ms = dayToMs(day); if (ms != null) p.setActual(ms); }}
                    onCommit={(day) => { const ms = dayToMs(day); if (ms != null) p.setActual(ms); }}
                  />
                </span>
                <span className={`rag ${p.variance.tone}`}>{p.variance.text}</span>
              </span>
            </div>
            <div className="ho-snap-row">
              <span className="ho-snap-k">Scope delivered{p.provisional ? ' (provisional)' : ''}</span>
              <span className="ho-snap-v">
                <b>{p.scope.shipped} of {p.scope.total} {p.scope.total === 1 ? 'card' : 'cards'} shipped</b>
                <span className="rag n">
                  {p.phaseOpen && p.scope.open > 0
                    ? `${p.scope.open} still open · ${p.scope.cut} deferred`
                    : `${p.scope.deferred} deferred`}
                </span>
              </span>
            </div>
            {p.baseline != null && (
              <div className="ho-snap-row">
                <span className="ho-snap-k">Budget at Go-Live</span>
                <span className="ho-snap-v">
                  <b>{fmtMoney(p.forecast ?? p.baseline)} of {fmtMoney(p.baseline)}</b>
                  {p.budgetPct != null && p.budgetPct !== 0 && (
                    <span className={`rag ${p.budgetPct > 0 ? 'r' : 'g'}`}>
                      {p.budgetPct > 0 ? '+' : ''}{p.budgetPct}%
                    </span>
                  )}
                </span>
              </div>
            )}
          </div>

          <div className="ho-reassure">
            <Icon name="info" size={15} />
            <span>
              {p.phaseOpen ? (
                <>If you mark it live now, the <b>{p.scope.open} open card{p.scope.open === 1 ? '' : 's'} carry over</b> as
                the first service work, and the as-delivered record notes a delivery phase was still running at Go-Live.
                Nothing is lost — you can keep working them on the in-service board.</>
              ) : (
                <>Nothing is deleted. The charter, phases and budget at Go-Live are kept as this product's{' '}
                <b>as-delivered record</b>. The <b>same board</b> flips to continuous — no new board — and its
                features then count as <b>Change</b>, its fixes as <b>Run</b>. Change that any time in Settings.</>
              )}
            </span>
          </div>

          <div className="ho-foot">
            <Icon name="clock" size={14} />
            <span>
              Records a delivery event in <b>Workspace History</b> and the steering pack — <b>went live → in
              service</b>, with actual vs planned Go-Live.
            </span>
          </div>

          {p.problem && <p className="id-problem">{p.problem}</p>}
        </div>

        <div className="modal-actions ho-actions">
          <button className="ho-dismiss" onClick={dismiss} disabled={p.busy}>Not yet</button>
          <div className="ho-actions-right">
            {p.phaseOpen && (
              <button className="btn btn-small" onClick={p.onOpenPlan} disabled={p.busy}>Open the plan</button>
            )}
            <button className="btn btn-primary btn-small" onClick={p.onConfirm} disabled={p.busy}>
              {p.phaseOpen ? 'Mark it Live anyway' : 'Mark it Live · Move to Service'}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function dayFromMsLocal(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}
